/**
 * Copyright © 2026 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import {
  ProductCard,
  type ProductQuantityControl as ProductCardQuantityControl,
  useCartContext,
} from "@farmlands/commerce";
import { toast } from "@farmlands/shadcn-ui";
import dayjs from "dayjs";
import { ReactNode, useCallback, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import { usePermission } from "../../../../hooks/usePermission";
import { selectRemoteConfig } from "../../../../services/firebase/remote-config/remoteConfig.slice";
import { useAppDispatch } from "../../../../store/rootStore";
import { ScrollYPositionKeys } from "../../../../util/consts";
import { isValidDate, saveScrollYPosition } from "../../../../util/helpers";
import {
  selectCanUseQuickOrder,
  selectCurrentFarmerAccount,
} from "../../../azure-b2c/storage/auth.slice";
import CartSlice, { selectCurrentCart } from "../../../cart/storage/cart.slice";
import { CartItem } from "../../../cart/storage/cart.types";
import { mapToCommerceAddToCartItem } from "../../../cart/utils/commerce-cart-mapping";
import { QuoteModalType } from "../../../quotes/components/QuoteModal/types";
import { useQuoteModal } from "../../../quotes/components/QuoteModal/useQuoteModal";
import { PRODUCT_CATEGORY, PRODUCTS_ROUTES, STOCK_AVAILABILITY } from "../../constants";
import { usePriceAndAvailability } from "../../hooks/usePriceAndAvailability";
import { Order, Product, StockAvailabilityType, usePastProductItemNumbers } from "../../storage";
import { selectProductAvailabilities } from "../../storage/stocks.slice";
import { mapItemToCardProduct, PRODUCT_CARD_PLACEHOLDER } from "../../utils/card-product";
import { getPromotionBadges } from "../../utils/getPromotionBadges";
import { useProductStatus } from "../../utils/useProductStatus";
import {
  EProductLocation,
  openProductDetailsLink,
} from "../ProductDetails/utils/openProductDetails";

import { ProductVariantDetails } from "./ProductVariantDetails";
import { ProductType } from "./types";

export interface ShopProductCardProps {
  item: Product | CartItem;
  isPastProduct?: boolean;
  enableQuote?: boolean;
  testId?: string;
}

export const ShopProductCard = ({
  item,
  isPastProduct,
  enableQuote,
  testId,
}: ShopProductCardProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const quoteProvider = useQuoteModal();
  const canUseQuickOrder = useSelector(selectCanUseQuickOrder);
  const currentAccount = useSelector(selectCurrentFarmerAccount);
  // Read only for the delivery-mode/pickup-store fields usePriceAndAvailability needs —
  // that data doesn't exist in the new CartContext yet (Phase 6).
  const cart = useSelector(selectCurrentCart);
  const remoteConfig = useSelector(selectRemoteConfig);
  const canCreateCartOrder = usePermission("FarmlandsProShop_CartOrderCreate").allowed;
  const stockAvailabilitiesFromSlice =
    useSelector(selectProductAvailabilities(item.productId)) ?? [];
  const cartUpdatedTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const { items: cartItems, addToCart, setQuantity: setCartQuantity } = useCartContext();

  const isTfo = currentAccount?.hasTfoAccess;
  const { isDisabled, canPurchase, noPriceAvailable } = useProductStatus(
    item.lifeCycleState,
    item.basePriceExcGst
  );

  const itemNumbers = usePastProductItemNumbers(currentAccount?.accountNumber);

  const isAllProduct: boolean = !isPastProduct;
  const productType: ProductType = isPastProduct ? ProductType.Past : ProductType.All;

  const lastOrderedDate: Date | undefined = isPastProduct
    ? new Date(Math.max(...item.orders.map((x) => new Date(x.createdAt ?? Date.now()).getTime())))
    : undefined;

  const stockAvailabilities: StockAvailabilityType[] = item.stockAvailabilities?.length
    ? item.stockAvailabilities
    : stockAvailabilitiesFromSlice;

  const notifyCartUpdated = useCallback((): void => {
    toast.dismiss("cart-updated");
    clearTimeout(cartUpdatedTimeoutRef.current);

    cartUpdatedTimeoutRef.current = setTimeout(() => {
      toast.success("Cart updated", {
        id: "cart-updated",
      });
    }, 1000);
  }, []);

  useEffect(() => {
    window.addEventListener("cartUpdated", notifyCartUpdated);
    return () => {
      window.removeEventListener("cartUpdated", notifyCartUpdated);
      clearTimeout(cartUpdatedTimeoutRef.current);
    };
  }, [notifyCartUpdated]);

  const productDetailsNavigate = useCallback((): void => {
    openProductDetailsLink(item, isAllProduct, productType, EProductLocation.SHOP, navigate);
  }, [item, isAllProduct, productType, navigate]);

  const onTitleClick = useCallback((): void => {
    const sessionStorageKey: ScrollYPositionKeys = ScrollYPositionKeys.Products;
    if (pathname === PRODUCTS_ROUTES.BASE) {
      saveScrollYPosition({ sessionStorageKey });
    } else {
      saveScrollYPosition({ sessionStorageKey, position: 0 });
    }

    productDetailsNavigate();
  }, [pathname, productDetailsNavigate]);

  const handleEnquireOptions = useCallback((): void => {
    quoteProvider.setQuoteType(QuoteModalType.ENQUIRY);
    quoteProvider.setProduct(item);
  }, [quoteProvider, item]);

  const inCart = cartItems.find((entry) => entry.productId === item.productId);

  const showActions: boolean = !!canUseQuickOrder && canPurchase;

  const quantity: ProductCardQuantityControl | undefined =
    showActions &&
    canCreateCartOrder &&
    inCart &&
    !item.hasVariants &&
    item.category !== PRODUCT_CATEGORY.SERVICES
      ? {
          value: inCart.quantity,
          onChange: (value: number) => {
            if (value > 0 && currentAccount) {
              const reduxItem = cart?.items.find((entry) => entry.productId === item.productId);
              if (reduxItem) {
                dispatch(
                  CartSlice.actions.setQuantity({
                    accountNumber: currentAccount.accountNumber,
                    product: reduxItem,
                    amount: value,
                  })
                );
              }
              setCartQuantity(item.productId, value);
              notifyCartUpdated();
            }
          },
        }
      : undefined;

  const onShowOptions: (() => void) | undefined =
    showActions && item.hasVariants ? productDetailsNavigate : undefined;

  const onEnquire: (() => void) | undefined =
    showActions &&
    canCreateCartOrder &&
    !isTfo &&
    item.stockAvailability === STOCK_AVAILABILITY.ENQUIRE &&
    !item.hasVariants
      ? handleEnquireOptions
      : undefined;

  const handleAddToCart = useCallback((): void => {
    if (!currentAccount) {
      return;
    }

    const product = {
      ...item,
      quantity: 1,
      productId: parseInt(item.productId.toString(), 10),
    };

    dispatch(
      CartSlice.actions.addToCart({
        accountNumber: currentAccount.accountNumber,
        product,
      })
    );
    addToCart(mapToCommerceAddToCartItem(product, 1));
    notifyCartUpdated();
  }, [item, addToCart, notifyCartUpdated, currentAccount, dispatch]);

  const onAddToCart: (() => void) | undefined =
    showActions &&
    canCreateCartOrder &&
    item.category !== PRODUCT_CATEGORY.SERVICES &&
    !item.hasVariants &&
    !inCart &&
    (isTfo || item.stockAvailability !== STOCK_AVAILABILITY.ENQUIRE)
      ? handleAddToCart
      : undefined;

  const { price, stock, lifecycleNotice } = usePriceAndAvailability({
    item,
    isTfo,
    homeBranchId: currentAccount?.homeBranchId,
    priceQuantity: inCart?.quantity,
    noPriceAvailable,
    stockAvailabilities,
    cart,
  });

  const previouslyOrderedNote: string | undefined =
    isAllProduct && itemNumbers.has(item.itemNumber)
      ? item.hasVariants
        ? "You've ordered a variation of this product before."
        : "You've ordered this product before."
      : undefined;

  const lastOrderedLabel: string | undefined =
    isPastProduct && isValidDate(lastOrderedDate)
      ? `Last ordered ${dayjs(lastOrderedDate).format("DD MMM YYYY")}`
      : undefined;

  const totalOrderedQty: number = item.orders
    ?.map((order: Order) => order.quantity)
    .reduce((accumulator: number, current: number) => accumulator + current, 0);

  const variants: ReactNode | undefined = !isAllProduct ? (
    <ProductVariantDetails item={item} totalOrderedQty={totalOrderedQty} />
  ) : undefined;

  const promotionBadges = getPromotionBadges(
    item.promotions,
    item.priceBreaks,
    remoteConfig.traderDealsFilterValue,
    remoteConfig.traderDealsEnabled,
    remoteConfig.buyBreaksEnabled
  ).map((badge) => badge.label);

  const isClickable: boolean = !isDisabled;

  return (
    <ProductCard
      testId={testId}
      onTitleClick={isClickable ? onTitleClick : undefined}
      product={mapItemToCardProduct(item)}
      placeholderSrc={
        remoteConfig.enableShopProductDetailsPage ? undefined : PRODUCT_CARD_PLACEHOLDER
      }
      price={price}
      stock={stock}
      statusBadge={lifecycleNotice}
      promotionBadges={promotionBadges}
      previouslyOrderedNote={previouslyOrderedNote}
      lastOrderedLabel={lastOrderedLabel}
      variants={variants}
      quantity={quantity}
      onShowOptions={onShowOptions}
      onEnquire={onEnquire}
      onAddToCart={onAddToCart}
      onRequestQuote={
        enableQuote && quoteProvider.isQuoteable(item) && !currentAccount?.hasTfoAccess
          ? () => {
              quoteProvider.setProduct(item);
              quoteProvider.setQuoteType(QuoteModalType.QUOTE_SINGLE);
            }
          : undefined
      }
      requestQuoteLabel="Request a quote"
    />
  );
};

export default ShopProductCard;
